import EventEmitter from "eventemitter3";
import { EventTypes } from "../../../../../core/event-bus";
import { Context } from "../../../../../models/context";
import { Data } from "../../../../../models/data";
import { IDataItem, IDataSize, IItemOrderTypeSize } from "../../../../../models/data.interface";
import { OnlineOrderingUtil } from "../../../../../online-ordering/online-ordering-util";
import { LocalizationService } from "../../../../../services/localization-service";
import { Util } from "../../../../../utils/util";
import { CustomizeItemSectionHeader } from "./customize-item-section-header";
import { HalfModifierSelection } from "../../../../../types/enums";
import { CustomizeItemModifierBase } from "../base/customize-item-modifier-base";
import { ICustomizeItemModifierBaseElement } from "../base/customize-item-modifier-base.interface";
import { CustomizeItemModifierSingleItem } from "../single-item/customize-item-modifier";

export interface ICustomizeItemSizeSection {
    menuItems: IDataItem[],
    orderTypeId: number,
    sizeId: number,
    allowedSizes: number[]
}

interface ICustomizeItemSizeSectionModel {
    sizeId: number,
    sizes: CustomizeItemModifierBase<ICustomizeItemModifierBaseElement>[]
}

/**
 * Renders and manages the "Size" selection section of a customizable menu item.
 */
export class CustomizeItemSizeSection {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _containerElement: HTMLElement | null;
    private readonly _menuItems: IDataItem[];
    private readonly _state: ICustomizeItemSizeSectionModel;

    /**
     * @param {EventEmitter} eventBus - Event system for inter-component communication.
     * @param {Data} data - Source for global application data.
     * @param {Context} context - Runtime context including selected store, language, etc.
     * @param {LocalizationService} localizationService - Service for localized UI strings.
     * @param {ICustomizeItemSizeSection} params - Parameters for rendering the size section.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, params: ICustomizeItemSizeSection) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._menuItems = params.menuItems;

        this._state = this._createModel(params.sizeId);
        this._containerElement = this._createSizeSectionElement(params.orderTypeId, params.sizeId, params.allowedSizes);
    }

    /**
     * Gets the DOM element representing the size section.
     * @returns {HTMLElement | null} The rendered element or null if not available.
     */
    public get element(): HTMLElement | null {
        return this._containerElement;
    }

    /**
     * Gets the currently selected size ID.
     * @returns {number} The selected size ID.
     */
    public get sizeId(): number {
        return this._state.sizeId;
    }

    /**
     * Gets the available size options for this item.
     * @returns {CustomizeItemModifierBase[]} The list of selectable sizes.
     */
    public get sizes(): CustomizeItemModifierBase<ICustomizeItemModifierBaseElement>[] {
        return this._state.sizes;
    }

    /**
     * Initializes the internal model with the selected size ID.
     *
     * @param {number} sizeId - The size ID to preselect.
     * @returns {ICustomizeItemSizeSectionModel} The initialized model.
     */
    private _createModel(sizeId: number): ICustomizeItemSizeSectionModel {
        return {
            sizeId: sizeId,
            sizes: []
        };
    }

    /**
     * Updates the selected size ID and emits an event for external consumers.
     *
     * @param {number} sizeId - The new selected size ID.
     */
    private _updateSizeId(sizeId: number): void {
        this._state.sizeId = sizeId;
        this._eventBus.emit(EventTypes.CUSTOMIZE_ITEM_SIZE_CHANGE, sizeId);
    }

    /**
     * Creates a section UI for selecting item sizes based on order type and allowed sizes.
     * Filters out invalid or unavailable sizes and builds a radio-style modifier UI.
     *
     * @param {number} orderTypeId - The current order type (e.g., delivery, pickup).
     * @param {number} sizeId - The currently selected size ID.
     * @param {number[]} allowedSizes - An optional list of size IDs allowed for the item.
     * @returns {HTMLElement | null} The size section DOM element, or null if no sizes apply.
     */
    private _createSizeSectionElement(orderTypeId: number, sizeId: number, allowedSizes: number[]): HTMLElement | null {
        const sizes = this._getFilteredSizes(orderTypeId, sizeId, allowedSizes);
        if (!sizes.length)
            return null;

        const groupHasImage = sizes.some(x => x.ImageURL && x.ImageURL.trim() !== "");
        const wrapperDiv = this._buildSizeOptionsWrapper(sizes, sizeId, allowedSizes, groupHasImage)

        const header = new CustomizeItemSectionHeader(this._localizationService, this._localizationService.names("Size")).element;
        const parentDiv = document.createElement("div");
        parentDiv.classList.add("modifiers-selection-parent");
        parentDiv.appendChild(header);
        parentDiv.appendChild(wrapperDiv);

        return parentDiv;
    }

    /**
     * Retrieves and filters item sizes based on the given order type and allowed size IDs.
     * If allowedSizes are provided, only those sizes will be retained.
     * Also ensures the selected size ID is valid, and updates it if necessary.
     *
     * @param {number} orderTypeId - The order type (e.g., delivery or pickup).
     * @param {number} sizeId - The currently selected size ID.
     * @param {number[]} allowedSizes - A list of allowed size IDs for filtering.
     * @returns {IItemOrderTypeSize[]} A filtered array of sizes applicable for the item.
     */
    private _getFilteredSizes(orderTypeId: number, sizeId: number, allowedSizes: number[]): IItemOrderTypeSize[] {
        let commonSizes: IItemOrderTypeSize[] = [];

        for (const [index, menuItem] of this._menuItems.entries()) {
            const sizes = OnlineOrderingUtil.GetItemSizes(menuItem, orderTypeId);

            if (index === 0) {
                // First item: start with its sizes
                commonSizes = sizes;
            } else {
                // Keep only sizes that exist in both
                const sizeIds = new Set(sizes.map(s => s.SizeId));
                commonSizes = commonSizes.filter(s => sizeIds.has(s.SizeId));
            }
        }

        // If there's only one size and no size selected yet, skip rendering
        if (commonSizes.length === 1 && sizeId === 0)
            return [];

        // Reset state to force selection update later
        if(!allowedSizes.includes(sizeId))
            this._updateSizeId(-1);

        if (allowedSizes.length > 0) {
            const validSizes = commonSizes.filter(s => allowedSizes.includes(s.SizeId));
            if (validSizes.length > 0)
                commonSizes = validSizes;

            // If the currently selected size is no longer valid, reset to the first valid one
            const isValidSizeSelected = commonSizes.some(s => s.SizeId === sizeId);
            if (!isValidSizeSelected || (isValidSizeSelected && commonSizes.length === 1))
                this._updateSizeId(commonSizes[0].SizeId);
        }

        return commonSizes;
    }

    /**
     * Builds the HTML wrapper containing the list of size options as radio-style modifier elements.
     * Each option is localized and conditionally rendered depending on QR availability and allowed sizes.
     *
     * @param {IItemOrderTypeSize[]} sizes - The sizes to display as options.
     * @param {number} sizeId - The currently selected size ID.
     * @param {number[]} allowedSizes - A list of allowed size IDs for validation and default selection.
     * @param {boolean} groupHasImage - Indicates whether any of the sizes contain an image (affects layout).
     * @returns {HTMLElement} A container `<div>` element containing the size options.
     */
    private _buildSizeOptionsWrapper(
        sizes: IItemOrderTypeSize[],
        sizeId: number,
        allowedSizes: number[],
        groupHasImage: boolean
    ): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.classList.add("customize-item-modifier-wrapper");
        wrapper.setAttribute("role", "listbox");
        wrapper.setAttribute("aria-label", this._localizationService.names("Size"));

        const isQRCode = this._context.getProperty("qrCode");

        for (const size of sizes) {
            // Skip sizes not offered via QR if applicable
            if (isQRCode && !size.IsOfferedQR)
                continue;

            const mSize = this._getSizeById(size.SizeId);
            if (!mSize)
                continue;

            const name = this._localizationService.GetName(mSize.Names);
            const desc = this._localizationService.GetDescription(mSize.Descriptions);
            const label = desc ? `${name} (${desc})` : name;

            const isSelected = allowedSizes.length > 0
                ? allowedSizes.length === 1 || size.SizeId === this._state.sizeId
                : size.SizeId === sizeId;

            const option = new CustomizeItemModifierSingleItem(
                this._eventBus,
                this._data,
                this._context,
                this._localizationService,
                {
                    modifierDisplayGroup: null,
                    modifierId: size.SizeId,
                    leftModifier: null,
                    rightModifier: null,
                    preModifierId: null,
                    halfId: HalfModifierSelection.WHOLE,
                    id: `rdo_customize_item_size_${size.SizeId}`,
                    name: label,
                    radioId: "rdo_customize_item_size",
                    imageUrl: size.ImageURL,
                    menuItem: this._menuItems[0],
                    groupHasImage,
                    isSelected,
                    isMultiSelect: false,
                    isHalfMode: false,
                    isSize: true
                }
            );

            this._state.sizes.push(option);

            const div = document.createElement("div");
            div.appendChild(option.element);
            wrapper.appendChild(div);

            if (isSelected && allowedSizes.length === 0) {
                this._updateSizeId(sizeId);
            }
        }

        return wrapper;
    }

    /**
     * Looks up a size definition from global data by its ID.
     *
     * @param {number} sizeId - The ID of the size to find.
     * @returns {IDataSize | null} The matching size object or null if not found.
     */
    private _getSizeById(sizeId: number): IDataSize | null {
        const sizes = this._data.getProperty("Sizes");
        const size = sizes.find(x => x.SizeId === sizeId);
        return size || null;
    }
}